// src/components/ResultEntryDialog.jsx
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./ui/Modal";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Button from "./ui/Button";
import Spinner from "./ui/Spinner";
import Skeleton from "./ui/Skeleton";
import Stopwatch from "./Stopwatch";
import Countdown from "./Countdown";
import Summenrechner from "./Summenrechner";
import TeamQrScanner from "./TeamQrScanner";
import { Filter, FilterX, X, QrCode, CheckCircle2, CloudOff, RotateCcw, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { teamKey } from "../utils/teamKey";
import { fetchWithAuth, mitSitzungspruefung } from "../utils/fetchWithAuth";
import { triggerSessionExpired } from "../utils/authSession";
import { teilweiseAusgesetzt, ausgesetztText } from "../utils/ausgesetzt";
import {
  sendeEintrag,
  einreihen,
  entfernen as ausWarteschlangeEntfernen,
  warteschlange,
  abonnieren,
  alleSenden,
  beiGesendet,
} from "../utils/eintragsWarteschlange";

const LS_KEYS = {
  EVENT_FILTER: "resultEntry_eventFilter",
  ENTRY_BY: "resultEntry_entryBy",
};

/** Vorhandenen Wert lesbar machen: Schaetzung bzw. Leistung plus Einheit. */
function formatWert(eintrag, stationType, unit) {
  const wert = stationType === "estimate" ? eintrag.estimate_value : eintrag.performance_score;
  if (wert === undefined || wert === null || wert === "") return "–";
  return `${String(wert).replace(".", ",")}${unit ? " " + unit : ""}`;
}

/** " um 14:03" aus einem ISO-Zeitstempel, leer wenn nicht lesbar. */
function formatUhrzeit(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return ` um ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function ResultEntryDialog({
  open,
  onClose,
  station,
  teams,
  onSaved,
  onEditingChange,
  // Laedt die Team-Liste frisch und gibt sie zurueck (Station.jsx). Fuer
  // Nachmeldungen: der QR-Scan sucht damit ein zweites Mal, und der Betreuer
  // kann die Liste von Hand aktualisieren.
  onTeamsNeuLaden,
}) {
  const [entriesByTeam, setEntriesByTeam] = useState({});
  const [formData, setFormData] = useState({
    performance_score: "",
    estimate_value: "",
    entry_by: "",
  });
  const [eventFilter, setEventFilter] = useState("all");
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // Eintrag, den ein ANDERES Geraet angelegt hat, waehrend dieser Dialog offen
  // war (Antwort 409). Erst nach dieser Rueckmeldung darf ersetzt werden.
  const [konflikt, setKonflikt] = useState(null); // { team, entry }

  // Letzte Speicherung – Grundlage fuer die Bestaetigung im Dialog und fuer
  // "Rueckgaengig". { team, teamName, art: "gesendet"|"lokal", wertText,
  // entry, vorher, queueId, queueVorher }
  const [letzteSpeicherung, setLetzteSpeicherung] = useState(null);
  const [undoLaeuft, setUndoLaeuft] = useState(false);
  // Wurde seit dem Oeffnen etwas gespeichert? Dann laedt die Station-Seite
  // beim Schliessen einmal neu – nicht nach jedem einzelnen Team, sonst
  // wuerde das Neuladen mitten in die naechste Eingabe fallen.
  const gespeichertRef = useRef(false);

  // Noch nicht gesendete Eintraege dieser Station (lokale Warteschlange).
  const [queue, setQueue] = useState(() => warteschlange());
  useEffect(() => abonnieren(setQueue), []);

  // Nachgesendete Eintraege dieser Station als erfasst uebernehmen.
  const stationsNummer = Number(station?.station_number);
  useEffect(
    () =>
      beiGesendet((entry) => {
        if (!entry || Number(entry.station_number) !== stationsNummer) return;
        setEntriesByTeam((prev) => ({ ...prev, [entry.team_id]: entry }));
      }),
    [stationsNummer]
  );

  // 🔍 Suche & Anzeigeoptionen
  const [searchTerm, setSearchTerm] = useState("");
  // Standard: alle Teams sichtbar, erfasste nur optisch abgesetzt – niemand soll
  // mehr denken, ein Team fehle, weil es (wie frueher) einfach ausgeblendet war.
  const [hideCompleted, setHideCompleted] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const stationType = station?.station_type || "normal";
  // Kurze Einheiten (cm, s, kg) passen ins Ergebnisfeld, laengere ("Karten/Fuizl")
  // wuerden die Eingabe ueberlagern und stehen deshalb in der Beschriftung.
  const unitLang = String(station?.unit || "").trim().length > 4;

  // Optionale Stoppuhr fuer Zeitspiele. "off" | "total" | "per_member".
  // Nur bei Leistungsstationen sinnvoll – bei einer Schaetzstation gibt es keine
  // Zeit zu messen. Sie ist eine reine Eingabehilfe; gespeichert wird weiterhin
  // der Zahlenwert im Ergebnisfeld, den man auch von Hand eintragen kann.
  const stopwatchMode = station?.stopwatch_mode || "off";
  const showStopwatch = stopwatchMode !== "off" && stationType !== "estimate";
  // Optionale Maximalzeit (Sekunden, 0 = keine): danach stoppt die Uhr von selbst.
  const stopwatchMaxSeconds = Math.max(0, Math.round(Number(station?.stopwatch_max_seconds) || 0));
  // Versuche je Teilnehmer (nur "pro Teilnehmer"; 1/fehlend = ein Lauf, der beste zaehlt).
  const stopwatchAttempts = Math.max(1, Math.round(Number(station?.stopwatch_attempts) || 1));

  // Optionaler Countdown mit Klingelton (Sekunden, 0/fehlend = aus). Reine
  // Hilfe fuer Spiele mit fester Spielzeit; liefert kein Ergebnis.
  const countdownSeconds = Math.max(0, Math.round(Number(station?.countdown_seconds) || 0));
  const showCountdown = countdownSeconds > 0 && stationType !== "estimate";

  // Optionaler Summenrechner (ein Wert je Person). Nur bei Leistungsstationen;
  // eine Schaetzung ist ein einzelner Wert.
  const showSumme = station?.person_sum === true && stationType === "normal";

  // `selectedTeam` haelt seit der ID-Umstellung den Bezugsschluessel, nicht den
  // Namen. Fuer Anzeige und Suchfeld wird der Name hierueber nachgeschlagen.
  const teamsByKey = useMemo(() => {
    const map = new Map();
    (Array.isArray(teams) ? teams : []).forEach((t) => {
      const k = teamKey(t);
      if (k) map.set(k, t);
    });
    return map;
  }, [teams]);

  const nameVon = (key) => teamsByKey.get(key)?.team_name ?? "";

  // Server-Eintraege plus lokal wartende Eintraege dieser Station. Wartende
  // gelten fuer die Anzeige als "erfasst" (mit Kennzeichen `_ausstehend`),
  // damit niemand dasselbe Team ein zweites Mal eintraegt, nur weil der erste
  // Wert noch auf dem Geraet liegt.
  const erfasstByTeam = useMemo(() => {
    const nummer = Number(station?.station_number);
    const map = { ...entriesByTeam };
    for (const q of queue) {
      if (Number(q.payload.station_number) !== nummer) continue;
      map[q.payload.team_id] = {
        ...q.payload,
        timestamp: q.erstelltAm,
        _ausstehend: true,
        _queueStatus: q.status,
      };
    }
    return map;
  }, [entriesByTeam, queue, station]);

  // Bereits vorhandenes Ergebnis des gewaehlten Teams – beim Oeffnen geladen,
  // lokal wartend oder per 409 vom Server gemeldet. Nur wenn es hier steht (und
  // damit im Dialog sichtbar ist), wird beim Speichern ersetzt.
  const bestehenderEintrag =
    (konflikt && konflikt.team === selectedTeam ? konflikt.entry : null) ||
    erfasstByTeam[selectedTeam] ||
    null;

  // Mitgliedernamen des aktuell gewaehlten Teams (member1..member5, leere
  // ausgelassen) – Grundlage fuer den Pro-Teilnehmer-Modus der Stoppuhr.
  const memberNames = useMemo(() => {
    const team = teamsByKey.get(selectedTeam);
    if (!team) return [];
    return ["member1", "member2", "member3", "member4", "member5"]
      .map((k) => (typeof team[k] === "string" ? team[k].trim() : ""))
      .filter(Boolean);
  }, [teamsByKey, selectedTeam]);

  const eventFilterRef = useRef(null);
  const searchInputRef = useRef(null);
  const teamSelectRef = useRef(null);
  const scoreInputRef = useRef(null);
  const entryByInputRef = useRef(null);

  // Zuletzt genutzten Namen beim Oeffnen vorbelegen.
  // BEHOBENER FEHLER: Der Effekt hing an `formData.entry_by` und schrieb den
  // gespeicherten Namen jedes Mal zurueck, sobald das Feld leer war – wer den
  // Namen loeschen wollte, um einen anderen einzutippen, sah ihn sofort wieder.
  useEffect(() => {
    if (!open) return;
    const savedName = localStorage.getItem(LS_KEYS.ENTRY_BY);
    if (savedName) {
      setFormData((prev) => (prev.entry_by ? prev : { ...prev, entry_by: savedName }));
    }
  }, [open]);


  // gespeicherte Filter laden
  useEffect(() => {
    const savedFilter = localStorage.getItem(LS_KEYS.EVENT_FILTER);
    if (savedFilter) setEventFilter(savedFilter);
  }, []);

  // beim Öffnen Fokus & Reset
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSelectedTeam("");
      setShowSuggestions(false);
      setKonflikt(null);
      setLetzteSpeicherung(null);
      gespeichertRef.current = false;
      if (eventFilterRef.current) {
        setTimeout(() => eventFilterRef.current?.focus(), 100);
      }
    }
  }, [open]);

  // vorhandene Einträge laden
  useEffect(() => {
    if (!open || !station) return;
    const loadEntries = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // fetchWithAuth statt rohem fetch: eine abgelaufene Sitzung fuehrt so
        // zur Neuanmeldung statt zu einer unverstaendlichen Fehlermeldung.
        const entries = await fetchWithAuth(
          `${BACKEND_URL}/entries?station=${encodeURIComponent(station.station_number)}`,
          null
        );
        // Nach Bezugsschluessel indizieren (`team_id`; Rueckfall auf den
        // Freitextnamen fuer Eintraege von vor der ID-Umstellung).
        const entriesMap = (entries || []).reduce((acc, entry) => {
          const key = entry.team_id ?? entry.team ?? entry.team_name;
          if (key) acc[key] = entry;
          return acc;
        }, {});
        setEntriesByTeam(entriesMap);
      } catch (err) {
        setError(
          "Bisherige Einträge konnten nicht geladen werden – bereits erfasste Teams sind " +
            "deshalb nicht markiert. (" + err.message + ")"
        );
      } finally {
        setIsLoading(false);
      }
    };
    loadEntries();
  }, [open, station, BACKEND_URL]);

  // Teams filtern nach Kategorie
  useEffect(() => {
    if (open && Array.isArray(teams)) {
      let filtered =
        eventFilter === "all"
          ? teams
          : teams.filter((t) => t.event_type === eventFilter);

      if (station) {
        if (station.allow_groups === "Erwachsene") {
          filtered = filtered.filter((t) => t.event_type !== "Kinder");
        } else if (station.allow_groups === "Kinder") {
          filtered = filtered.filter((t) => t.event_type !== "Erwachsene");
        }
      }

      setFilteredTeams(filtered);
    }
  }, [open, teams, eventFilter, station]);

  // Formular nur bei Kategorie- oder Stationswechsel leeren. BEHOBENER FEHLER:
  // das hing frueher am obigen Effekt und lief damit auch, wenn nur die
  // Team-Liste neu geladen wurde (Hintergrund-Aktualisierung) – eine gerade
  // getippte Eingabe verschwand dann kommentarlos.
  useEffect(() => {
    if (!open) return;
    setFormData((prev) => ({ performance_score: "", estimate_value: "", entry_by: prev.entry_by }));
    setError(null);
  }, [open, eventFilter, station]);

  // Signal an Elternkomponente, wenn der Benutzer aktiv tippt
useEffect(() => {
  if (!open) return;
  const handleInput = () => {
    if (onEditingChange) onEditingChange(true);
    clearTimeout(window._editTimeout);
    window._editTimeout = setTimeout(() => {
      if (onEditingChange) onEditingChange(false);
    }, 3000); // nach 3 Sekunden Inaktivität wieder freigeben
  };
  window.addEventListener("input", handleInput);
  return () => {
    window.removeEventListener("input", handleInput);
    if (onEditingChange) onEditingChange(false);
  };
}, [open, onEditingChange]);

  // Vorbefüllen bei Teamwechsel (bzw. sobald die Eintraege geladen sind).
  // BEHOBENER FEHLER: Der Effekt hing auch an `teamsByKey` und lief deshalb bei
  // jeder Hintergrund-Aktualisierung der Team-Liste – und ueberschrieb dabei
  // die gerade getippte Eingabe mit dem alten Wert.
  useEffect(() => {
    if (selectedTeam) {
      const existingEntry = erfasstByTeam[selectedTeam];
      const lastUsedName = localStorage.getItem(LS_KEYS.ENTRY_BY);
      setFormData({
        performance_score: existingEntry?.performance_score ?? "",
        estimate_value: existingEntry?.estimate_value ?? "",
        entry_by: existingEntry?.entry_by ?? lastUsedName ?? "",
      });
      setError(null);
      scoreInputRef.current?.focus();
      setSearchTerm(nameVon(selectedTeam));
      setShowSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam, isLoading]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canSave = () => {
    if (!selectedTeam || !formData.entry_by || isSaving) return false;
    if (stationType === "estimate") return formData.estimate_value !== "";
    return formData.performance_score !== "";
  };

  // Formular fuer das naechste Team vorbereiten. Der Name des Eintragenden
  // bleibt stehen – an einer Station traegt meist dieselbe Person ein.
  const naechstesTeam = () => {
    setSelectedTeam("");
    setSearchTerm("");
    setKonflikt(null);
    setShowSuggestions(false);
    setFormData((prev) => ({ performance_score: "", estimate_value: "", entry_by: prev.entry_by }));
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const handleSave = async () => {
    if (!canSave()) {
      setError("Bitte alle Felder ausfüllen.");
      return;
    }

    setIsSaving(true);
    setError(null);

    localStorage.setItem(LS_KEYS.EVENT_FILTER, eventFilter);
    localStorage.setItem(LS_KEYS.ENTRY_BY, formData.entry_by);

    const vorher = bestehenderEintrag;
    const payload = {
      // Kanonischer Bezug: die stabile Team-ID. Ein Tippfehler im Suchfeld kann
      // dadurch kein Ergebnis mehr einem nicht existierenden Team zuordnen.
      team_id: selectedTeam,
      station_number: Number(station.station_number),
      entry_by: formData.entry_by,
      // BEHOBENER FEHLER: stand fest auf `true`. Damit war der Konfliktschutz
      // des Backends (409) wirkungslos – trug ein zweites Geraet fuer dasselbe
      // Team ein, wurde dessen Wert still ueberschrieben. Jetzt nur, wenn der
      // bestehende Wert im Dialog angezeigt wird und der Knopf "Ersetzen" heisst.
      replaceIfExists: !!vorher,
    };
    // Ersetzt wird nur genau der Stand, den der Nutzer gesehen hat; hat ihn
    // inzwischen jemand geaendert, meldet der Server einen Konflikt.
    if (vorher && !vorher._ausstehend && vorher.timestamp) {
      payload.expected_timestamp = vorher.timestamp;
    }

    if (stationType === "estimate") {
      payload.estimate_value = Number(formData.estimate_value);
    } else {
      payload.performance_score = Number(formData.performance_score);
    }

    const team = selectedTeam;
    const teamName = nameVon(team);
    const info = { team_name: teamName, station_name: station?.station_name, unit: station?.unit };
    const wertText = formatWert(payload, stationType, station?.unit);

    // Offline zwischenspeichern statt verwerfen.
    const lokalSpeichern = () => {
      const { id, vorher: queueVorher } = einreihen(payload, info);
      gespeichertRef.current = true;
      setLetzteSpeicherung({ team, teamName, art: "lokal", wertText, queueId: id, queueVorher });
      naechstesTeam();
    };

    try {
      // Liegt fuer dieses Team schon ein ungesendeter Wert auf dem Geraet, wird
      // der nur aktualisiert: direkt zu senden, waehrend der alte noch in der
      // Warteschlange steht, koennte die Reihenfolge vertauschen.
      if (vorher && vorher._ausstehend) {
        lokalSpeichern();
        alleSenden(BACKEND_URL);
        return;
      }

      const r = await sendeEintrag(BACKEND_URL, payload);

      if (r.art === "ok") {
        setEntriesByTeam((prev) => ({ ...prev, [team]: r.entry }));
        gespeichertRef.current = true;
        setLetzteSpeicherung({
          team,
          teamName,
          art: "gesendet",
          wertText,
          entry: r.entry,
          vorher: vorher && !vorher._ausstehend ? vorher : null,
        });
        naechstesTeam();
        return;
      }
      if (r.art === "konflikt") {
        // Ein anderes Geraet war schneller. Nichts ueberschreiben, sondern den
        // vorhandenen Wert zeigen; ein zweiter Klick auf "Ersetzen" gilt dann
        // als bewusste Entscheidung.
        setKonflikt({ team, entry: r.entry });
        setError(
          "Für dieses Team wurde inzwischen an einem anderen Gerät ein Ergebnis eingetragen. " +
            "Prüfe den Wert oben – „Ersetzen“ überschreibt ihn."
        );
        return;
      }
      if (r.art === "abgelehnt") {
        setError(r.fehler);
        return;
      }
      if (r.art === "auth") {
        // Nicht verlieren: zwischenspeichern, nach der Anmeldung wird gesendet.
        einreihen(payload, info);
        toast.warn(
          "Sitzung abgelaufen – das Ergebnis ist auf dem Gerät gespeichert und wird nach der " +
            "Anmeldung automatisch gesendet.",
          { autoClose: 8000 }
        );
        triggerSessionExpired();
        return;
      }
      // r.art === "netz"
      lokalSpeichern();
    } finally {
      setIsSaving(false);
    }
  };

  // Letzte Speicherung zuruecknehmen.
  //  - lokal: aus der Warteschlange nehmen (bzw. den vorherigen lokalen Wert
  //    wiederherstellen)
  //  - gesendet, vorher leer: Eintrag loeschen
  //  - gesendet, vorher ein Wert: diesen Wert zurueckschreiben
  // Beide Server-Wege nennen den Stand, den sie zuruecknehmen wollen. Hat
  // inzwischen jemand anderes geaendert, wird nichts angefasst.
  const handleUndo = async () => {
    const l = letzteSpeicherung;
    if (!l) return;
    setError(null);

    if (l.art === "lokal") {
      ausWarteschlangeEntfernen(l.queueId, l.queueVorher);
      setLetzteSpeicherung(null);
      toast.info(`${l.teamName}: Eingabe zurückgenommen`);
      return;
    }

    setUndoLaeuft(true);
    try {
      const nummer = Number(station.station_number);
      if (l.vorher) {
        const alt = {
          team_id: l.team,
          station_number: nummer,
          entry_by: l.vorher.entry_by || formData.entry_by,
          replaceIfExists: true,
          expected_timestamp: l.entry.timestamp,
        };
        if (stationType === "estimate") alt.estimate_value = Number(l.vorher.estimate_value);
        else alt.performance_score = Number(l.vorher.performance_score);

        const r = await sendeEintrag(BACKEND_URL, alt);
        if (r.art !== "ok") {
          setError(
            r.art === "konflikt"
              ? "Rückgängig nicht möglich: der Wert wurde inzwischen an einem anderen Gerät geändert."
              : `Rückgängig nicht möglich: ${r.fehler || "keine Verbindung"}`
          );
          return;
        }
        setEntriesByTeam((prev) => ({ ...prev, [l.team]: r.entry }));
      } else {
        const res = await fetch(
          `${BACKEND_URL}/entries/${encodeURIComponent(l.entry.id)}?expected_timestamp=${encodeURIComponent(l.entry.timestamp)}`,
          { method: "DELETE", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        ).catch(() => null);
        mitSitzungspruefung(res);
        if (!res || !res.ok) {
          // Vorher meldete auch eine abgelaufene Sitzung "keine Verbindung".
          setError(
            res && res.status === 409
              ? "Rückgängig nicht möglich: der Wert wurde inzwischen an einem anderen Gerät geändert."
              : res && res.status === 401
                ? "Rückgängig nicht möglich – die Sitzung ist abgelaufen. Bitte neu anmelden und dann korrigieren."
                : "Rückgängig nicht möglich – keine Verbindung. Bitte später korrigieren."
          );
          return;
        }
        setEntriesByTeam((prev) => {
          const neu = { ...prev };
          delete neu[l.team];
          return neu;
        });
      }
      setLetzteSpeicherung(null);
      toast.info(`${l.teamName}: Eingabe zurückgenommen`);
    } finally {
      setUndoLaeuft(false);
    }
  };

  // Schliessen: Station-Seite einmal aktualisieren, falls etwas gespeichert wurde.
  const schliessen = () => {
    if (gespeichertRef.current && onSaved) onSaved(Number(station?.station_number));
    onClose();
  };

  const handleKeyDown = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) nextRef.current.focus();
      else if (canSave()) handleSave();
    }
  };

  // QR-Code vom Laufzettel auswerten. Der Code kodiert die Team-URL
  // "<frontend>/team/<share_token>" (siehe functions/pdf/data.js); der Token
  // ist gegen das ohnehin geladene `teams`-Array pruefbar, ohne Zusatzrequest.
  const [teamsLaden, setTeamsLaden] = useState(false);

  /** Team-Liste frisch holen; Rueckgabe die neue Liste oder null bei Fehler. */
  const teamsAktualisieren = async () => {
    if (!onTeamsNeuLaden) return null;
    setTeamsLaden(true);
    try {
      return await onTeamsNeuLaden();
    } catch (err) {
      setError(`Team-Liste konnte nicht aktualisiert werden (${err.message}).`);
      return null;
    } finally {
      setTeamsLaden(false);
    }
  };

  const handleQrScan = async (decodedText) => {
    setScannerOpen(false);
    const match = String(decodedText).match(/\/team\/([A-Za-z0-9]+)/);
    const token = (match ? match[1] : decodedText).trim().toUpperCase();
    const suche = (liste) =>
      (Array.isArray(liste) ? liste : []).find((t) => (t.share_token || "").toUpperCase() === token);

    let team = suche(teams);
    // Unbekannt? Vielleicht eine Nachmeldung, die dieses Geraet noch nicht
    // kennt – einmal frisch laden und erneut suchen.
    if (!team) {
      const frisch = await teamsAktualisieren();
      if (frisch) team = suche(frisch);
    }

    if (!team) {
      setError("QR-Code passt zu keinem bekannten Team – auch nicht nach dem Aktualisieren der Team-Liste.");
      return;
    }
    if (station?.allow_groups === "Erwachsene" && team.event_type === "Kinder") {
      setError(`${team.team_name} ist an dieser Station nicht zugelassen (nur Erwachsene).`);
      return;
    }
    if (station?.allow_groups === "Kinder" && team.event_type === "Erwachsene") {
      setError(`${team.team_name} ist an dieser Station nicht zugelassen (nur Kinder).`);
      return;
    }

    setError(null);
    // Kategorie-Filter passend nachziehen, damit das gescannte Team in der
    // Liste auftaucht, egal welche Kategorie zuvor eingestellt war.
    if (team.event_type === "Kinder" || team.event_type === "Erwachsene") {
      setEventFilter(team.event_type);
    }
    setSelectedTeam(teamKey(team));
  };

  // Anzahl bereits erfasster Teams in der aktuellen Kategorie – Grundlage fuer
  // den sichtbaren Hinweistext, wenn der Toggle sie ausblendet.
  const completedCount = filteredTeams.filter(
    (t) => !!erfasstByTeam[teamKey(t)]
  ).length;

  // Sichtbare Teams berechnen: offene Teams zuerst, erfasste Teams werden nicht
  // mehr versteckt, sondern ans Ende sortiert und optisch abgesetzt – ausser der
  // Toggle blendet sie bewusst aus.
  const visibleTeams = filteredTeams
    .filter((t) => (t.team_name || "").toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((t) => !hideCompleted || !erfasstByTeam[teamKey(t)])
    .sort((a, b) => {
      const aDone = !!erfasstByTeam[teamKey(a)];
      const bDone = !!erfasstByTeam[teamKey(b)];
      return aDone === bDone ? 0 : aDone ? 1 : -1;
    });

  const showAutoComplete =
    searchTerm.length >= 3 && showSuggestions && visibleTeams.length > 0;

  return (
    <Modal
      open={open}
      onClose={schliessen}
      title={`${station?.station_name || "Station"} – Ergebnis`}
      footer={
        <>
          <Button variant="secondary" onClick={schliessen} disabled={isSaving}>
            Fertig
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave()}>
            {isSaving ? (
              <>
                <Spinner size={4} /> Speichern…
              </>
            ) : bestehenderEintrag ? (
              "Ersetzen"
            ) : (
              "Speichern"
            )}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <div className="space-y-4 relative">
          {teilweiseAusgesetzt(station) && (
            <div className="p-3 rounded-lg border border-red-500 bg-red-500/10 text-sm font-semibold">
              ⏸ Diese Station ist für {ausgesetztText(station)} ausgesetzt – dafür können keine
              Ergebnisse eingetragen werden.
            </div>
          )}
          {/* Bestaetigung der letzten Speicherung mit "Rueckgaengig". Der Dialog
              bleibt nach dem Speichern offen – das naechste Team kann sofort
              eingetragen werden. */}
          {letzteSpeicherung && (
            <div
              className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
                letzteSpeicherung.art === "lokal"
                  ? "border-yellow-600 bg-yellow-900/40 text-yellow-100"
                  : "border-green-700 bg-green-900/30 text-green-100"
              }`}
            >
              {letzteSpeicherung.art === "lokal" ? (
                <CloudOff className="w-5 h-5 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="font-medium">
                  {letzteSpeicherung.teamName}: {letzteSpeicherung.wertText}{" "}
                  {letzteSpeicherung.art === "lokal" ? "auf dem Gerät gespeichert" : "gespeichert"}
                </div>
                {letzteSpeicherung.art === "lokal" && (
                  <div className="text-xs opacity-80">
                    Keine Verbindung – wird automatisch gesendet, sobald das Netz wieder da ist.
                  </div>
                )}
              </div>
              <button
                onClick={handleUndo}
                disabled={undoLaeuft}
                className="flex items-center gap-1 px-2 py-1 rounded border border-current text-xs font-semibold hover:bg-white/10 disabled:opacity-50 min-h-[36px]"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Rückgängig
              </button>
            </div>
          )}

          {/* Kategorieauswahl */}
          <Select
            ref={eventFilterRef}
            label="Kategorie"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            options={[
              { value: "all", label: "Alle Teams" },
              { value: "Erwachsene", label: "Erwachsene" },
              { value: "Kinder", label: "Kinder" },
            ]}
          />

          {/* Suchfeld + Toggle */}
          <div className="flex items-center space-x-2 relative">
            <div className="relative flex-1">
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Team suchen..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="pr-11"
              />
              {/* Clear Button */}
              {searchTerm.length > 0 && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedTeam("");
                    setShowSuggestions(false);
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center min-w-[44px] min-h-[44px] text-gray-400 hover:text-white"
                  title="Suche löschen"
                >
                  <X className="w-5 h-5" />
                </button>
              )}

              {/* Autocomplete */}
              {showAutoComplete && (
                <div className="absolute z-20 mt-1 w-full bg-secondary-900 border border-secondary-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {visibleTeams.map((t) => {
                    const already = erfasstByTeam[teamKey(t)];
                    return (
                      <div
                        key={teamKey(t)}
                        onClick={() => {
                          setSelectedTeam(teamKey(t));
                          setSearchTerm(t.team_name);
                          setShowSuggestions(false);
                        }}
                        className={`px-3 py-2 cursor-pointer hover:bg-secondary-700 flex justify-between items-center ${
                          already ? "text-gray-500 italic" : "text-white"
                        }`}
                      >
                        <span>{t.team_name}</span>
                        {already && (
                          <span className="text-xs text-primary-400">
                            {already._ausstehend ? "⏳ nicht gesendet" : "✅ erfasst"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Toggle Button */}
            <button
              onClick={() => setHideCompleted((v) => !v)}
              className={`flex items-center justify-center p-2 rounded-lg border transition-colors ${
                hideCompleted
                  ? "bg-green-600 border-green-500 text-white hover:bg-green-500"
                  : "bg-secondary-800 border-secondary-700 text-gray-400 hover:bg-secondary-700"
              }`}
              title={
                hideCompleted
                  ? "Erfasste Teams ausgeblendet – klicken zum Einblenden"
                  : "Erfasste Teams werden angezeigt – klicken zum Ausblenden"
              }
            >
              {hideCompleted ? <Filter className="w-4 h-4" /> : <FilterX className="w-4 h-4" />}
            </button>

            {/* QR-Scan Button */}
            <button
              onClick={() => setScannerOpen(true)}
              className="flex items-center justify-center p-2 rounded-lg border bg-secondary-800 border-secondary-700 text-gray-400 hover:bg-secondary-700 hover:text-white transition-colors"
              title="Team per QR-Code auf dem Laufzettel auswählen"
            >
              <QrCode className="w-4 h-4" />
            </button>
          </div>

          {/* Sichtbarer Hinweis statt reinem Icon-Tooltip: macht den aktiven
              Filter explizit, damit kein Team scheinbar "fehlt". */}
          {hideCompleted && completedCount > 0 && (
            <div className="text-xs text-gray-400 -mt-2">
              {completedCount} {completedCount === 1 ? "Team" : "Teams"} mit Ergebnis ausgeblendet
            </div>
          )}

          {/* Team-Auswahl (Fallback) */}
          <Select
            ref={teamSelectRef}
            label="Team"
            value={selectedTeam}
            onClick={() => {
              // Suchfeld leeren & alle Teams anzeigen
              setSearchTerm("");
              setShowSuggestions(false);
            }}
            onFocus={() => {
              setSearchTerm("");
              setShowSuggestions(false);
            }}
            onChange={(e) => setSelectedTeam(e.target.value)}
            options={[
              { value: "", label: "Team auswählen..." },
              ...filteredTeams
                .filter((t) => (!hideCompleted || !erfasstByTeam[teamKey(t)]))
                .sort((a, b) => {
                  const aDone = !!erfasstByTeam[teamKey(a)];
                  const bDone = !!erfasstByTeam[teamKey(b)];
                  return aDone === bDone ? 0 : aDone ? 1 : -1;
                })
                .map((t) => {
                  const done = erfasstByTeam[teamKey(t)];
                  return {
                    // Wert ist der Bezugsschluessel, Label bleibt der Klarname.
                    value: teamKey(t),
                    label: `${done ? (done._ausstehend ? "⏳ " : "✅ ") : ""}${t.team_name} (${t.event_type})`,
                    muted: done,
                  };
                }),
            ]}
          />


          {/* Nachmeldungen kommen automatisch an (bis zu zwei Minuten); wer
              nicht warten will, holt die Liste hier sofort. */}
          {onTeamsNeuLaden && (
            <button
              type="button"
              onClick={async () => {
                setError(null);
                const frisch = await teamsAktualisieren();
                if (frisch) toast.info(`Team-Liste aktualisiert (${frisch.length} Teams)`, { toastId: "teams-neu" });
              }}
              disabled={teamsLaden}
              className="-mt-2 flex items-center gap-1 text-xs text-secondary-400 hover:text-white disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${teamsLaden ? "animate-spin" : ""}`} />
              {teamsLaden ? "Team-Liste wird geladen…" : "Team fehlt? Liste neu laden"}
            </button>
          )}

          {/* Optionaler Countdown mit Klingelton. Startbar erst mit gewaehltem Team
              (wie die Stoppuhr); je Team frisch, damit nie ein Lauf des vorigen
              Teams weiterklingt. */}
          {showCountdown && (
            <Countdown
              key={`${station?.station_number}-${countdownSeconds}-${selectedTeam}`}
              seconds={countdownSeconds}
              disabled={isSaving || !selectedTeam}
              hinweis={!selectedTeam ? "Zum Starten zuerst ein Team wählen." : undefined}
            />
          )}

          {/* Optionale Stoppuhr (nur Zeitspiele, Team muss gewaehlt sein) */}
          {showStopwatch && selectedTeam && (
            <Stopwatch
              key={`${selectedTeam}-${stopwatchMode}-${stopwatchMaxSeconds}-${stopwatchAttempts}`}
              mode={stopwatchMode}
              maxSeconds={stopwatchMaxSeconds}
              scoreDirection={station?.score_direction || "higher"}
              maxVersuche={stopwatchAttempts}
              memberNames={memberNames}
              disabled={isSaving}
              onApply={(sekunden) => handleFormChange("performance_score", sekunden)}
            />
          )}

          {/* Optionaler Summenrechner: ein Wert je Person, die Summe geht ins
              Ergebnisfeld. Je Team frisch, damit nie Werte des vorigen bleiben. */}
          {showSumme && selectedTeam && (
            <Summenrechner
              key={selectedTeam}
              memberNames={memberNames}
              unit={station?.unit}
              disabled={isSaving}
              onApply={(summe) => handleFormChange("performance_score", summe)}
            />
          )}

          {/* Hinweis auf ein bereits erfasstes Ergebnis: macht sichtbar, dass
              Speichern hier einen vorhandenen Wert ersetzt. */}
          {selectedTeam && bestehenderEintrag && (
            <div className="p-3 rounded-lg border border-yellow-600 bg-yellow-900/40 text-yellow-100 text-sm">
              <div className="font-medium">
                {bestehenderEintrag._ausstehend
                  ? "Bereits erfasst – noch nicht gesendet"
                  : "Bereits erfasst"}
              </div>
              <div>
                {formatWert(bestehenderEintrag, stationType, station?.unit)}
                {bestehenderEintrag.entry_by ? ` – von ${bestehenderEintrag.entry_by}` : ""}
                {formatUhrzeit(bestehenderEintrag.timestamp)}
              </div>
              <div className="text-xs text-yellow-200/80 mt-1">
                „Ersetzen“ überschreibt diesen Wert.
              </div>
            </div>
          )}

          {/* Ergebnis und Eintragender nebeneinander – spart eine Zeile Hoehe */}
          <div className="grid grid-cols-2 gap-3 items-start">
          {/* Ergebnisfeld; kurze Einheiten (cm, s, kg) stehen im Feld, lange in der
              Beschriftungszeile, weil sie sonst die Eingabe ueberlagern */}
          <div className="flex flex-col space-y-1">
            <label className="flex items-baseline justify-between gap-2 text-sm text-gray-400">
              <span className="shrink-0">
                {stationType === "estimate" ? "Schätzwert" : "Ergebnis"}
              </span>
              {unitLang && (
                <span
                  className="min-w-0 truncate text-primary-500"
                  title={station.unit}
                >
                  in {station.unit}
                </span>
              )}
            </label>
            <div className="relative">
              <input
                ref={scoreInputRef}
                type="number"
                inputMode="decimal"
                step={showStopwatch ? "0.01" : undefined}
                value={
                  stationType === "estimate"
                    ? formData.estimate_value
                    : formData.performance_score
                }
                onChange={(e) =>
                  handleFormChange(
                    stationType === "estimate"
                      ? "estimate_value"
                      : "performance_score",
                    e.target.value
                  )
                }
                onKeyDown={(e) => handleKeyDown(e, entryByInputRef)}
                placeholder={stationType === "estimate" ? "z. B. 42" : "z. B. 123"}
                disabled={!selectedTeam || isSaving}
                className={`w-full bg-secondary-800 border border-secondary-700 text-white rounded-lg px-3 py-2 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  station?.unit && !unitLang ? "pr-12" : ""
                }`}
              />
              {station?.unit && !unitLang && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-500 text-sm pointer-events-none">
                  {station.unit}
                </span>
              )}
            </div>
          </div>

          <Input
            ref={entryByInputRef}
            type="text"
            label="Eintragender"
            value={formData.entry_by}
            onChange={(e) => handleFormChange("entry_by", e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, null)}
            disabled={!selectedTeam || isSaving}
          />
          </div>

          {error && (
            <div className="p-3 bg-red-800 text-red-200 rounded">
              {error}
            </div>
          )}

          <div className="pb-1 text-xs text-secondary-400 text-center">
            Enter: Nächstes Feld bzw. Speichern
          </div>
        </div>
      )}

      <TeamQrScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleQrScan}
      />
    </Modal>
  );
}
